/**
 * AD/Entra Sync — runs list page (admin).
 *
 * Lists recent ad_sync_runs with status, counts, and a link into
 * the per-run proposals page. Includes a "Run new sync" button
 * that invokes ad-sync-fetch.
 */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useAdSyncRuns, useTriggerAdSyncFetch } from "../hooks/useAdSync";
import { useToast } from "../components/ui/Toast";
import Button from "../components/common/Button";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import type { AdSyncRunStatus } from "../types/adSync.types";

const STATUS_STYLES: Record<AdSyncRunStatus, string> = {
  running: "bg-blue-100 text-blue-800",
  complete: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

function StatusBadge({ status }: { status: AdSyncRunStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

const AdSyncRuns: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data: runs, isLoading, error } = useAdSyncRuns();
  const triggerFetch = useTriggerAdSyncFetch();
  const [triggering, setTriggering] = useState(false);

  const handleTriggerFetch = async () => {
    setTriggering(true);
    try {
      const result = await triggerFetch.mutateAsync();
      if (result?.error) {
        showToast("error", `Failed to start sync: ${result.error}`);
        return;
      }
      showToast("success", "Sync started. New run will appear shortly.");
      if (result?.run_id) {
        navigate(`/admin/ad-sync/runs/${result.run_id}`);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      showToast("error", `Failed to start sync: ${msg}`);
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-4">
        <Link
          to="/sales-team"
          className="text-sm text-gray-500 hover:text-gray-900"
        >
          ← Back to Sales Team
        </Link>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AD / Entra Sync</h1>
          <p className="text-sm text-gray-600 mt-1">
            Recent Microsoft Entra sync runs. Click a row to review the proposed
            changes from a run.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleTriggerFetch}
          isLoading={triggering}
          disabled={triggering}
        >
          {triggering ? "Starting…" : "Run new sync"}
        </Button>
      </div>

      <Card>
        {isLoading && (
          <div className="p-12 flex justify-center">
            <LoadingSpinner />
          </div>
        )}
        {error && (
          <div className="p-6 text-sm text-red-800 bg-red-50 border border-red-200 rounded">
            Failed to load sync runs: {String(error)}
          </div>
        )}
        {!isLoading && !error && (runs?.length ?? 0) === 0 && (
          <div className="p-12 text-center text-gray-500">
            No sync runs yet. Click <strong>Run new sync</strong> to start the
            first one.
          </div>
        )}
        {!isLoading && (runs?.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Started
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Users
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Proposals computed
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {runs!.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {format(new Date(r.started_at), "PP p")}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.source}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {r.user_count ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {r.proposals_computed_at
                        ? format(new Date(r.proposals_computed_at), "PP p")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/ad-sync/runs/${r.id}`}
                        className="text-red-600 hover:text-red-700 font-medium text-sm"
                      >
                        Review →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdSyncRuns;
